import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeStudioOptions } from '@/i18n/studioModuleText';
import {
  DEFAULT_MODEL_TRY_ON_ETHNICITY,
  DEFAULT_MODEL_TRY_ON_GARMENT_TYPE,
  DEFAULT_MODEL_TRY_ON_MODEL_TYPE,
  MODEL_STUDIO_GENERATE_COUNT_OPTIONS,
  MODEL_TRY_ON_ETHNICITY_OPTIONS,
  MODEL_TRY_ON_MODEL_TYPE_OPTIONS,
  getModelStudioConfig,
  resolveGarmentTypeOptionsForModel,
  sanitizeModelTryOnParams,
} from '@/pages/ipe/workbench/constants/modelStudioConfig';
import { useTryOnPresets } from '@/pages/ipe/workbench/hooks/useTryOnPresets';
import { useModelEthnicityPresets } from '@/pages/ipe/workbench/hooks/useModelEthnicityPresets';
import {
  buildTryOnPresetComboKey,
  buildTryOnSystemModelSelectionParams,
  resolveTryOnPresetSelectionFields,
} from '@/pages/ipe/workbench/utils/tryOnSystemModelPresetFallback';
import {
  buildModelEthnicityPresetComboKey,
  buildModelEthnicitySystemModelSelectionParams,
  clearInvalidModelEthnicityPreset,
  resolveEthnicityPresetFeatureCode,
  resolveModelEthnicityPresetSelectionFields,
} from '@/pages/ipe/workbench/utils/systemModelEthnicityPresetCatalog';
import IpeToolbarSelect from '@/pages/components/IpeToolbarSelect';
import ModelTryOnPresetPicker from './ModelTryOnPresetPicker';
import ModelEthnicityPresetPicker from './ModelEthnicityPresetPicker';
import '@/styles/ipe/workbench/Img2ImgPage.css';
import '@/styles/ipe/workbench/ModelStudioPage.css';

function renderStudioToolbarSelect({
  id,
  label,
  value,
  onChange,
  options,
  disabled = false,
  className = '',
  menuPlacement = 'down',
}) {
  return (
    <div className={`model-studio__field${className ? ` ${className}` : ''}`}>
      <label className="img2img-studio__field-label" htmlFor={id}>
        {label}
      </label>
      <IpeToolbarSelect
        id={id}
        className={`model-studio__toolbar-select${menuPlacement === 'up' ? ' model-studio__toolbar-select--up' : ''}`}
        variant="light"
        clearable={false}
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
        ariaLabel={label}
      />
    </div>
  );
}

function clearInvalidTryOnPreset(updates, prev, modelType, garmentType) {
  const presetId = String(prev.selectedPresetId || '').trim();
  if (!presetId) return updates;
  const prefix = `${modelType}-${garmentType}-`;
  if (presetId.startsWith(prefix)) {
    return updates;
  }
  return { ...updates, selectedPresetId: '', selectedPresetUrl: '' };
}

/** 与 ModelStudioPage 侧栏可对齐项（不含上传、指令案例、批量槽位） */
export default function ModelStudioStepParams({ navId, p, onChange, readOnly = false }) {
  const { t } = useTranslation('workbench');
  const studioConfig = useMemo(() => getModelStudioConfig(navId), [navId]);
  const isTryOn = navId === 'modelTryOn';
  const isHandheld = navId === 'modelHandheld';
  const isUniversalWear = navId === 'modelUniversalWear';
  const isPose = navId === 'modelPoseFission';
  const isEthnicityPresetNav = isHandheld || isUniversalWear;
  const showModelMeta = isTryOn || isEthnicityPresetNav;
  const ethnicityPresetFeatureCode = resolveEthnicityPresetFeatureCode(navId);

  const modelType = p.modelType || DEFAULT_MODEL_TRY_ON_MODEL_TYPE;
  const modelEthnicity = p.modelEthnicity || DEFAULT_MODEL_TRY_ON_ETHNICITY;
  const garmentType = p.garmentType || DEFAULT_MODEL_TRY_ON_GARMENT_TYPE;
  const garmentTypeOptions = useMemo(
    () => resolveGarmentTypeOptionsForModel(modelType),
    [modelType],
  );
  const tryOnComboRef = useRef('');
  const ethnicityComboRef = useRef('');

  const { presets: tryOnPresets, loading: tryOnPresetsLoading, fallbackUsed: tryOnPresetsFallbackUsed } = useTryOnPresets(
    isTryOn ? modelType : '',
    isTryOn ? garmentType : '',
  );
  const {
    presets: ethnicityPresets,
    loading: ethnicityPresetsLoading,
    fallbackUsed: ethnicityPresetsFallbackUsed,
  } = useModelEthnicityPresets(
    ethnicityPresetFeatureCode,
    isEthnicityPresetNav ? modelType : '',
    isEthnicityPresetNav ? modelEthnicity : '',
  );

  const generateCount = MODEL_STUDIO_GENERATE_COUNT_OPTIONS.includes(Number(p.generateCount))
    ? Number(p.generateCount)
    : 1;

  const promptMaxLength = studioConfig?.promptMaxLength || 500;
  const promptLabelKey = {
    '补充说明（可选）': 'workflowParams.extraPrompt',
    '手持效果（可选）': 'workflowParams.poseHandheld',
    '姿势描述（可选）': 'workflowParams.posePrompt',
    '模特风格（可选）': 'workflowParams.modelStyle',
  }[studioConfig?.promptLabel] || (isPose ? 'workflowParams.posePrompt' : 'workflowParams.extraPrompt');
  const promptLabel = t(promptLabelKey);
  const promptPlaceholder = studioConfig?.promptPlaceholder || '';

  useEffect(() => {
    if (!isTryOn || readOnly) return;
    const sanitized = sanitizeModelTryOnParams(p);
    if (sanitized.garmentType === garmentType && sanitized.modelType === modelType) return;
    onChange(clearInvalidTryOnPreset(sanitized, p, sanitized.modelType, sanitized.garmentType));
  }, [isTryOn, readOnly, modelType, garmentType, onChange, p]);

  useEffect(() => {
    if (!isTryOn || readOnly) return;

    const comboKey = buildTryOnPresetComboKey(modelType, garmentType);
    if (!comboKey) return;

    if (tryOnComboRef.current !== comboKey) {
      const isInitialSync = tryOnComboRef.current === '';
      tryOnComboRef.current = comboKey;
      if (!isInitialSync && (p.selectedPresetId || p.selectedPresetUrl)) {
        onChange({ selectedPresetId: '', selectedPresetUrl: '' });
      }
      if (!isInitialSync) return;
    }

    if (tryOnPresetsLoading) return;

    const nextSelection = resolveTryOnPresetSelectionFields({
      presets: tryOnPresets,
      selectedPresetId: p.selectedPresetId,
      modelType,
      garmentType,
    });
    const currentPresetId = String(p.selectedPresetId || '');
    const currentPresetUrl = String(p.selectedPresetUrl || '');
    if (
      nextSelection.selectedPresetId === currentPresetId
      && nextSelection.selectedPresetUrl === currentPresetUrl
    ) {
      return;
    }

    onChange(nextSelection);
  }, [
    isTryOn,
    readOnly,
    modelType,
    garmentType,
    tryOnPresets,
    tryOnPresetsLoading,
    p.selectedPresetId,
    p.selectedPresetUrl,
    onChange,
  ]);

  useEffect(() => {
    if (!isEthnicityPresetNav || readOnly) return;

    const comboKey = buildModelEthnicityPresetComboKey(modelType, modelEthnicity);
    if (!comboKey) return;

    if (ethnicityComboRef.current !== comboKey) {
      const isInitialSync = ethnicityComboRef.current === '';
      ethnicityComboRef.current = comboKey;
      if (!isInitialSync && (p.selectedPresetId || p.selectedPresetUrl)) {
        onChange({ selectedPresetId: '', selectedPresetUrl: '' });
      }
      if (!isInitialSync) return;
    }

    if (ethnicityPresetsLoading) return;

    const nextSelection = resolveModelEthnicityPresetSelectionFields({
      presets: ethnicityPresets,
      selectedPresetId: p.selectedPresetId,
      modelType,
      ethnicity: modelEthnicity,
    });
    const currentPresetId = String(p.selectedPresetId || '');
    const currentPresetUrl = String(p.selectedPresetUrl || '');
    if (
      nextSelection.selectedPresetId === currentPresetId
      && nextSelection.selectedPresetUrl === currentPresetUrl
    ) {
      return;
    }

    onChange(nextSelection);
  }, [
    isEthnicityPresetNav,
    readOnly,
    modelType,
    modelEthnicity,
    ethnicityPresets,
    ethnicityPresetsLoading,
    p.selectedPresetId,
    p.selectedPresetUrl,
    onChange,
  ]);

  const handleTryOnPresetSelect = useCallback((preset) => {
    onChange(buildTryOnSystemModelSelectionParams(preset));
  }, [onChange]);

  const handleEthnicityPresetSelect = useCallback((preset) => {
    onChange(buildModelEthnicitySystemModelSelectionParams(preset));
  }, [onChange]);

  if (!studioConfig && !isPose) {
    return <p className="wf-editor-params__muted">{t('workflowParams.noModelParams')}</p>;
  }

  return (
    <div className="img2img-page model-studio-page wf-studio-params-root wf-editor-model-studio">
      <section className="img2img-studio__section">
        <label className="img2img-studio__field-label" htmlFor={`wf-model-prompt-${navId}`}>
          {promptLabel}
        </label>
        <div className="img2img-studio__prompt-wrap">
          <textarea
            id={`wf-model-prompt-${navId}`}
            className="img2img-studio__prompt"
            placeholder={promptPlaceholder}
            value={p.prompt || ''}
            maxLength={promptMaxLength}
            disabled={readOnly}
            onChange={(e) => onChange({ prompt: e.target.value })}
          />
          <span className="img2img-studio__prompt-count">
            {(p.prompt || '').length}/{promptMaxLength}
          </span>
        </div>
      </section>

      {showModelMeta ? (
        <section className="img2img-studio__section model-studio__model-picker" aria-label={t('workflowParams.systemModel')}>
          <div className="model-studio__filter-bar">
            {renderStudioToolbarSelect({
              id: `wf-${navId}-model-type`,
              label: t('workflowParams.modelType'),
              value: modelType,
              disabled: readOnly,
              options: localizeStudioOptions(t, 'modelType', MODEL_TRY_ON_MODEL_TYPE_OPTIONS),
              className: 'model-studio__field--inline',
              onChange: (nextModelType) => {
                const sanitized = sanitizeModelTryOnParams({ ...p, modelType: nextModelType });
                if (isEthnicityPresetNav) {
                  onChange(clearInvalidModelEthnicityPreset(
                    sanitized,
                    p,
                    sanitized.modelType,
                    modelEthnicity,
                    ethnicityPresets,
                  ));
                  return;
                }
                onChange(clearInvalidTryOnPreset(sanitized, p, sanitized.modelType, sanitized.garmentType));
              },
            })}
            {isTryOn ? renderStudioToolbarSelect({
              id: `wf-${navId}-garment-type`,
              label: t('workflowParams.garmentType'),
              value: garmentType,
              disabled: readOnly,
              options: localizeStudioOptions(t, 'garment', garmentTypeOptions),
              className: 'model-studio__field--inline',
              menuPlacement: 'up',
              onChange: (nextGarmentType) => {
                const sanitized = sanitizeModelTryOnParams({ ...p, garmentType: nextGarmentType });
                onChange(clearInvalidTryOnPreset(sanitized, p, sanitized.modelType, sanitized.garmentType));
              },
            }) : null}
            {isEthnicityPresetNav ? renderStudioToolbarSelect({
              id: `wf-${navId}-model-ethnicity`,
              label: t('workflowParams.ethnicity'),
              value: modelEthnicity,
              disabled: readOnly,
              options: localizeStudioOptions(t, 'ethnicity', MODEL_TRY_ON_ETHNICITY_OPTIONS),
              className: 'model-studio__field--inline',
              menuPlacement: 'up',
              onChange: (nextEthnicity) => onChange(clearInvalidModelEthnicityPreset(
                { ...p, modelEthnicity: nextEthnicity },
                p,
                modelType,
                nextEthnicity,
                ethnicityPresets,
              )),
            }) : null}
          </div>

          {isTryOn ? (
            <ModelTryOnPresetPicker
              key={buildTryOnPresetComboKey(modelType, garmentType)}
              modelType={modelType}
              garmentType={garmentType}
              selectedPresetId={p.selectedPresetId || ''}
              paused={false}
              disabled={readOnly}
              onSelect={handleTryOnPresetSelect}
              presets={tryOnPresets}
              presetsLoading={tryOnPresetsLoading}
              presetsFallbackUsed={tryOnPresetsFallbackUsed}
            />
          ) : null}

          {isEthnicityPresetNav ? (
            <ModelEthnicityPresetPicker
              key={`${ethnicityPresetFeatureCode}-${buildModelEthnicityPresetComboKey(modelType, modelEthnicity)}`}
              featureCode={ethnicityPresetFeatureCode}
              modelType={modelType}
              ethnicity={modelEthnicity}
              selectedPresetId={p.selectedPresetId || ''}
              paused={false}
              disabled={readOnly}
              onSelect={handleEthnicityPresetSelect}
              presets={ethnicityPresets}
              presetsLoading={ethnicityPresetsLoading}
              presetsFallbackUsed={ethnicityPresetsFallbackUsed}
            />
          ) : null}
        </section>
      ) : null}

      <section className="img2img-studio__section">
        <div className="img2img-studio__field-row">
          <span className="img2img-studio__field-label">{t('workflowParams.qty')}</span>
          <span className="img2img-studio__field-hint">{t('workflowParams.qtyHint')}</span>
        </div>
        <div className="img2img-studio__qty-row" role="group" aria-label={t('workflowParams.qty')}>
          {MODEL_STUDIO_GENERATE_COUNT_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              className={`img2img-studio__qty-btn${generateCount === n ? ' is-active' : ''}`}
              onClick={() => onChange({ generateCount: n })}
              disabled={readOnly}
              aria-pressed={generateCount === n}
            >
              {n}
            </button>
          ))}
        </div>
      </section>

      <p className="wf-editor-params__muted wf-editor-fission__footnote">
        {isPose
          ? t('workflowParams.poseUpload')
          : t('workflowParams.modelUpload')}
      </p>
    </div>
  );
}
